import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Target, ArrowRight, Users, TrendingUp, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AddChallengeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    title: string;
    description: string;
    target_audience: string;
    kpi_name: string;
    kpi_base_value: string;
    kpi_expected_value: string;
    status: string;
  }) => void;
}

export function AddChallengeDialog({ open, onOpenChange, onSave }: AddChallengeDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_audience: '',
    kpi_name: '',
    kpi_base_value: '',
    kpi_expected_value: '',
  });
  const [orgChallenges, setOrgChallenges] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (!profile?.organization_id) return;
      const { data: cfg } = await supabase
        .from('coe_configuration')
        .select('prioritized_challenges')
        .eq('organization_id', profile.organization_id)
        .maybeSingle();
      const list = Array.isArray((cfg as any)?.prioritized_challenges)
        ? ((cfg as any).prioritized_challenges as Array<{ title?: string }>)
            .map((c) => c?.title)
            .filter(Boolean) as string[]
        : [];
      setOrgChallenges(list);
    })();
  }, [open]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isComplete = !!(formData.title && formData.description && formData.target_audience && formData.kpi_name);

  const handleSave = () => {
    onSave({ ...formData, status: 'active' });
    setFormData({ title: '', description: '', target_audience: '', kpi_name: '', kpi_base_value: '', kpi_expected_value: '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Agregar Reto Manualmente
          </DialogTitle>
          <DialogDescription>Define el reto de innovación que deseas resolver con IA</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          {orgChallenges.length > 0 && (
            <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/5">
              <Label className="flex items-center gap-2 text-sm">
                <Sparkles className="w-4 h-4 text-primary" />
                Retos priorizados por tu empresa
              </Label>
              <p className="text-xs text-muted-foreground">Toca uno para usarlo como punto de partida.</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {orgChallenges.map((c, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => handleChange('title', c)}
                  >
                    {c}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="title">Título del Reto *</Label>
            <Input id="title" placeholder="Ej: Automatizar reportes mensuales de ventas" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} className="bg-muted/50" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Descripción del Problema *</Label>
            <Textarea id="description" placeholder="Describe el problema que quieres resolver..." value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="bg-muted/50 min-h-[100px]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="target_audience" className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              Público Objetivo *
            </Label>
            <Input id="target_audience" placeholder="¿Quién se beneficiará de esta solución?" value={formData.target_audience} onChange={(e) => handleChange('target_audience', e.target.value)} className="bg-muted/50" />
          </div>
          <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border/50">
            <Label className="flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4 text-primary" />
              KPI de Impacto *
            </Label>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="kpi_name" className="text-sm text-muted-foreground">Nombre del KPI</Label>
                <Input id="kpi_name" placeholder="Ej: Tiempo de generación de reportes" value={formData.kpi_name} onChange={(e) => handleChange('kpi_name', e.target.value)} className="bg-background/50" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Valor Base</Label>
                  <Input placeholder="Ej: 8 horas" value={formData.kpi_base_value} onChange={(e) => handleChange('kpi_base_value', e.target.value)} className="bg-background/50" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Valor Esperado</Label>
                  <Input placeholder="Ej: 1 hora" value={formData.kpi_expected_value} onChange={(e) => handleChange('kpi_expected_value', e.target.value)} className="bg-background/50" />
                </div>
              </div>
            </div>
          </div>
          <Button onClick={handleSave} disabled={!isComplete} className="w-full gradient-primary" size="lg">
            Guardar Reto
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
